/**
 * Core game state management
 * Handles all game resources, upgrades, and progression logic
 */

export interface GameResources {
  quarks: number;
  protons: number;
  neutrons: number;
  energy: number;
  neutronStarMatter: number;
  
  // Elements
  hydrogen: number;      // H (1 proton)
  helium: number;        // He (2 protons + 2 neutrons)
  carbon: number;        // C (6 protons + 6 neutrons)
  nitrogen: number;      // N (7 protons + 7 neutrons)
  oxygen: number;        // O (8 protons + 8 neutrons)
  iron: number;          // Fe (26 protons + 30 neutrons) - Final element
}

export interface Upgrade {
  id: string;
  name: string;
  description: string;
  level: number;
  baseCost: number;
  costMultiplier: number;
  unlocked: boolean;
  effect?: (state: GameState) => void;
}

export interface PermanentUpgrade {
  id: string;
  name: string;
  description: string;
  level: number;
  maxLevel: number;
  baseCost: number;
  costMultiplier: number;
}

export interface GameStats {
  totalQuarksCollected: number;
  totalClicks: number;
  totalProtonsSynthesized: number;
  gameStartTime: number;
  lastUpdateTime: number;
  universeCycles: number; // Number of times universe has been reset
}

export type GameStage = 'subatomic' | 'atomic' | 'fusion' | 'supernova';

export interface UniverseEndState {
  isTriggered: boolean;
  startTime: number;
  phase: 'collapse' | 'singularity' | 'rebirth' | 'complete';
}

export interface ClickCombo {
  count: number;
  lastClickTime: number;
  isActive: boolean;
  expiresAt: number;
}

export type EnergyBoostLevel = 0 | 1 | 2 | 3 | 4;

export interface EnergyBoost {
  level: EnergyBoostLevel;  // 0 = off, 1 = 1.5x, 2 = 2x, 3 = 3x, 4 = 5x
  isActive: boolean;
  totalEnergySpent: number;  // Track total energy spent on boost
}

export const ENERGY_BOOST_CONFIG = {
  0: { multiplier: 1, energyPerSecond: 0, name: 'Off', color: '#888' },
  1: { multiplier: 1.5, energyPerSecond: 5, name: '1.5x Boost', color: '#4ECDC4' },
  2: { multiplier: 2, energyPerSecond: 15, name: '2x Turbo', color: '#FFD700' },
  3: { multiplier: 3, energyPerSecond: 40, name: '3x Surge', color: '#FF6B6B' },
  4: { multiplier: 5, energyPerSecond: 100, name: '5x Overdrive', color: '#9B59B6' },
} as const;

export class GameState {
  // Development mode - set to false for production
  readonly DEV_MODE = false; // 改为 false 发布时
  readonly DEV_SPEED_MULTIPLIER = 1000; // 开发模式速度倍率
  readonly DEV_COST_DIVISOR = 100; // 开发模式成本除数

  // Resources
  resources: GameResources = {
    quarks: 0,
    protons: 0,
    neutrons: 0,
    energy: 0,
    neutronStarMatter: 0,
    hydrogen: 0,
    helium: 0,
    carbon: 0,
    nitrogen: 0,
    oxygen: 0,
    iron: 0,
  };

  // Production rates (per second)
  production = {
    quarksPerSecond: 0,
    protonsPerSecond: 0,
    neutronsPerSecond: 0,
    energyPerSecond: 0,
  };

  // Upgrades
  upgrades: Map<string, Upgrade> = new Map();
  
  // Permanent upgrades (bought with neutron star matter)
  permanentUpgrades: Map<string, PermanentUpgrade> = new Map();

  // Stats
  stats: GameStats = {
    totalQuarksCollected: 0,
    totalClicks: 0,
    totalProtonsSynthesized: 0,
    gameStartTime: Date.now(),
    lastUpdateTime: Date.now(),
    universeCycles: 0,
  };

  // Click combo system (Quantum Fluctuation Multiplier)
  clickCombo: ClickCombo = {
    count: 0,
    lastClickTime: 0,
    isActive: false,
    expiresAt: 0,
  };

  // Energy boost system
  energyBoost: EnergyBoost = {
    level: 0,
    isActive: false,
    totalEnergySpent: 0,
  };

  // Current game stage
  currentStage: GameStage = 'subatomic';

  // Universe end state
  universeEnd: UniverseEndState = {
    isTriggered: false,
    startTime: 0,
    phase: 'collapse',
  };

  // Constants - Basic Particles
  readonly QUARKS_PER_PROTON = 3;
  readonly QUARKS_PER_NEUTRON = 3;
  readonly PROTON_BOOST_MULTIPLIER = 0.05; // 5% per proton (rebalanced)
  
  // Constants - Combo System
  readonly COMBO_THRESHOLD = 10;
  readonly COMBO_WINDOW = 2000;
  readonly COMBO_DURATION = 5000;
  readonly COMBO_MULTIPLIER = 2;
  
  // Constants - Element Recipes (protons + neutrons)
  readonly ELEMENTS = {
    hydrogen: { protons: 1, neutrons: 0, energy: 1 },
    helium: { protons: 2, neutrons: 2, energy: 4 },
    carbon: { protons: 6, neutrons: 6, energy: 12 },
    nitrogen: { protons: 7, neutrons: 7, energy: 20 },
    oxygen: { protons: 8, neutrons: 8, energy: 30 },
    iron: { protons: 26, neutrons: 30, energy: 100 },
  };
  
  // Constants - Upgrades
  readonly ELECTRON_ORBIT_MULTIPLIER = 0.5;
  
  // Constants - Universe End
  readonly UNIVERSE_END_THRESHOLD_ELEMENT = 'iron';
  readonly UNIVERSE_END_THRESHOLD_LEVEL = 10; // Need 10 iron atoms

  /**
   * Set energy boost level
   */
  setEnergyBoostLevel(level: EnergyBoostLevel): void {
    this.energyBoost.level = level;
    this.energyBoost.isActive = level > 0;
  }

  /**
   * Get current energy boost multiplier
   */
  getEnergyBoostMultiplier(): number {
    if (!this.energyBoost.isActive || this.energyBoost.level === 0) {
      return 1;
    }
    return ENERGY_BOOST_CONFIG[this.energyBoost.level].multiplier;
  }

  /**
   * Get current energy consumption rate for boost
   */
  getEnergyBoostConsumption(): number {
    if (!this.energyBoost.isActive || this.energyBoost.level === 0) {
      return 0;
    }
    return ENERGY_BOOST_CONFIG[this.energyBoost.level].energyPerSecond;
  }

  /**
   * Check if can afford energy boost at given level
   */
  canAffordEnergyBoost(level: EnergyBoostLevel): boolean {
    if (level === 0) return true;
    const consumption = ENERGY_BOOST_CONFIG[level].energyPerSecond;
    // Need at least 1 second worth of energy to activate
    return this.resources.energy >= consumption;
  }

  constructor() {
    this.initializeUpgrades();
    this.initializePermanentUpgrades();
  }

  /**
   * Initialize permanent upgrades (bought with neutron star matter)
   */
  private initializePermanentUpgrades(): void {
    this.permanentUpgrades.set('click-power', {
      id: 'click-power',
      name: 'Enhanced Click Power',
      description: 'Increases base click power by 100%',
      level: 0,
      maxLevel: 10,
      baseCost: 1,
      costMultiplier: 2,
    });

    this.permanentUpgrades.set('production-boost', {
      id: 'production-boost',
      name: 'Production Amplifier',
      description: 'Increases all production by 50%',
      level: 0,
      maxLevel: 10,
      baseCost: 2,
      costMultiplier: 2.5,
    });

    this.permanentUpgrades.set('cost-reduction', {
      id: 'cost-reduction',
      name: 'Cost Efficiency',
      description: 'Reduces all upgrade costs by 5%',
      level: 0,
      maxLevel: 10,
      baseCost: 3,
      costMultiplier: 3,
    });

    this.permanentUpgrades.set('auto-clicker', {
      id: 'auto-clicker',
      name: 'Auto-Clicker',
      description: 'Automatically clicks 1 time per second per level',
      level: 0,
      maxLevel: 5,
      baseCost: 5,
      costMultiplier: 4,
    });

    this.permanentUpgrades.set('starting-quarks', {
      id: 'starting-quarks',
      name: 'Quantum Reservoir',
      description: 'Start each cycle with 100 quarks per level',
      level: 0,
      maxLevel: 5,
      baseCost: 10,
      costMultiplier: 5,
    });
  }

  /**
   * Initialize all available upgrades
   */
  private initializeUpgrades(): void {
    // Quantum Fluctuation - Auto-generate quarks
    this.upgrades.set('quantum-fluctuation', {
      id: 'quantum-fluctuation',
      name: 'Quantum Fluctuation',
      description: 'Generates quarks automatically from quantum vacuum',
      level: 0,
      baseCost: 10,
      costMultiplier: 1.15,
      unlocked: true,
    });

    // Strong Interaction - Auto-synthesize protons
    this.upgrades.set('strong-interaction', {
      id: 'strong-interaction',
      name: 'Strong Interaction',
      description: 'Automatically converts quarks into protons',
      level: 0,
      baseCost: 100,
      costMultiplier: 1.15,
      unlocked: true,
    });

    // Proton Synthesizer - Manual proton creation
    this.upgrades.set('proton-synthesizer', {
      id: 'proton-synthesizer',
      name: 'Proton Synthesizer',
      description: 'Manually synthesize protons from quarks',
      level: 0,
      baseCost: 3, // Cost in quarks per synthesis
      costMultiplier: 1.0, // No cost increase for manual synthesis
      unlocked: false,
    });

    // Hydrogen Atom - First element
    this.upgrades.set('hydrogen-atom', {
      id: 'hydrogen-atom',
      name: 'Hydrogen Atom (H)',
      description: 'The first element. Generates energy automatically.',
      level: 0,
      baseCost: 1, // Cost in protons
      costMultiplier: 1.15,
      unlocked: false,
    });

    // Neutron Generator
    this.upgrades.set('neutron-generator', {
      id: 'neutron-generator',
      name: 'Neutron Generator',
      description: 'Generates neutrons automatically',
      level: 0,
      baseCost: 50,
      costMultiplier: 1.15,
      unlocked: false,
    });

    // Helium Atom
    this.upgrades.set('helium-atom', {
      id: 'helium-atom',
      name: 'Helium Atom (He)',
      description: 'Doubles hydrogen energy production',
      level: 0,
      baseCost: 2, // protons
      costMultiplier: 1.2,
      unlocked: false,
    });

    // Carbon Atom
    this.upgrades.set('carbon-atom', {
      id: 'carbon-atom',
      name: 'Carbon Atom (C)',
      description: 'The foundation of organic chemistry',
      level: 0,
      baseCost: 6,
      costMultiplier: 1.25,
      unlocked: false,
    });

    // Nitrogen Atom
    this.upgrades.set('nitrogen-atom', {
      id: 'nitrogen-atom',
      name: 'Nitrogen Atom (N)',
      description: 'Essential for life',
      level: 0,
      baseCost: 7,
      costMultiplier: 1.3,
      unlocked: false,
    });

    // Oxygen Atom
    this.upgrades.set('oxygen-atom', {
      id: 'oxygen-atom',
      name: 'Oxygen Atom (O)',
      description: 'The breath of existence',
      level: 0,
      baseCost: 8,
      costMultiplier: 1.35,
      unlocked: false,
    });

    // Iron Atom - Final element before supernova
    this.upgrades.set('iron-atom', {
      id: 'iron-atom',
      name: 'Iron Atom (Fe)',
      description: 'The stellar endpoint. Beyond this lies only collapse.',
      level: 0,
      baseCost: 26,
      costMultiplier: 1.5,
      unlocked: false,
    });

    // Fusion Reactor - Converts elements to energy
    this.upgrades.set('fusion-reactor', {
      id: 'fusion-reactor',
      name: 'Fusion Reactor',
      description: 'Harness the power of nuclear fusion',
      level: 0,
      baseCost: 100, // energy
      costMultiplier: 1.4,
      unlocked: false,
    });
  }

  /**
   * Calculate the cost of an upgrade at a given level
   */
  getUpgradeCost(upgradeId: string, level?: number): number {
    const upgrade = this.upgrades.get(upgradeId);
    if (!upgrade) return 0;

    const targetLevel = level !== undefined ? level : upgrade.level;
    let cost = Math.floor(upgrade.baseCost * Math.pow(upgrade.costMultiplier, targetLevel));

    // Apply cost reduction from permanent upgrades
    const costReduction = this.permanentUpgrades.get('cost-reduction')?.level || 0;
    if (costReduction > 0) {
      cost = Math.floor(cost * Math.pow(0.95, costReduction));
    }

    // Development mode: reduce costs
    if (this.DEV_MODE) {
      cost = Math.max(1, Math.floor(cost / this.DEV_COST_DIVISOR));
    }

    return cost;
  }

  /**
   * Calculate the cost of a permanent upgrade
   */
  getPermanentUpgradeCost(upgradeId: string): number {
    const upgrade = this.permanentUpgrades.get(upgradeId);
    if (!upgrade) return 0;

    let cost = Math.floor(upgrade.baseCost * Math.pow(upgrade.costMultiplier, upgrade.level));

    // Development mode: reduce costs
    if (this.DEV_MODE) {
      cost = Math.max(1, Math.floor(cost / this.DEV_COST_DIVISOR));
    }

    return cost;
  }

  /**
   * Check if a permanent upgrade can be purchased
   */
  canAffordPermanentUpgrade(upgradeId: string): boolean {
    const upgrade = this.permanentUpgrades.get(upgradeId);
    if (!upgrade || upgrade.level >= upgrade.maxLevel) return false;

    const cost = this.getPermanentUpgradeCost(upgradeId);
    return this.resources.neutronStarMatter >= cost;
  }

  /**
   * Purchase a permanent upgrade
   */
  purchasePermanentUpgrade(upgradeId: string): boolean {
    if (!this.canAffordPermanentUpgrade(upgradeId)) {
      return false;
    }

    const upgrade = this.permanentUpgrades.get(upgradeId)!;
    const cost = this.getPermanentUpgradeCost(upgradeId);

    this.resources.neutronStarMatter -= cost;
    upgrade.level += 1;

    return true;
  }

  /**
   * Check if an element can be synthesized
   */
  private canSynthesizeElement(elementId: keyof typeof this.ELEMENTS): boolean {
    const recipe = this.ELEMENTS[elementId];
    return this.resources.protons >= recipe.protons && 
           this.resources.neutrons >= recipe.neutrons;
  }

  /**
   * Check if an upgrade can be purchased
   */
  canAffordUpgrade(upgradeId: string): boolean {
    const upgrade = this.upgrades.get(upgradeId);
    if (!upgrade || !upgrade.unlocked) return false;

    const cost = this.getUpgradeCost(upgradeId);
    
    // Special case for proton synthesizer - costs quarks
    if (upgradeId === 'proton-synthesizer') {
      return this.resources.quarks >= cost;
    }

    // Element synthesis - check if we have enough protons and neutrons
    if (upgradeId === 'hydrogen-atom') {
      return this.canSynthesizeElement('hydrogen');
    }
    if (upgradeId === 'helium-atom') {
      return this.canSynthesizeElement('helium');
    }
    if (upgradeId === 'carbon-atom') {
      return this.canSynthesizeElement('carbon');
    }
    if (upgradeId === 'nitrogen-atom') {
      return this.canSynthesizeElement('nitrogen');
    }
    if (upgradeId === 'oxygen-atom') {
      return this.canSynthesizeElement('oxygen');
    }
    if (upgradeId === 'iron-atom') {
      return this.canSynthesizeElement('iron');
    }

    // Fusion reactor costs energy
    if (upgradeId === 'fusion-reactor') {
      return this.resources.energy >= cost;
    }

    // Default: costs quarks
    return this.resources.quarks >= cost;
  }

  /**
   * Synthesize an element
   */
  private synthesizeElement(elementId: keyof typeof this.ELEMENTS): boolean {
    const recipe = this.ELEMENTS[elementId];
    
    if (!this.canSynthesizeElement(elementId)) {
      return false;
    }

    // Deduct resources
    this.resources.protons -= recipe.protons;
    this.resources.neutrons -= recipe.neutrons;

    // Add element
    this.resources[elementId] += 1;

    return true;
  }

  /**
   * Purchase an upgrade
   */
  purchaseUpgrade(upgradeId: string): boolean {
    const upgrade = this.upgrades.get(upgradeId);
    if (!upgrade || !this.canAffordUpgrade(upgradeId)) {
      return false;
    }

    const cost = this.getUpgradeCost(upgradeId);

    // Special handling for proton synthesizer
    if (upgradeId === 'proton-synthesizer') {
      if (this.resources.quarks >= this.QUARKS_PER_PROTON) {
        this.resources.quarks -= this.QUARKS_PER_PROTON;
        this.resources.protons += 1;
        this.stats.totalProtonsSynthesized += 1;
        upgrade.level += 1;
        return true;
      }
      return false;
    }

    // Element synthesis
    if (upgradeId === 'hydrogen-atom' && this.synthesizeElement('hydrogen')) {
      upgrade.level += 1;
      this.updateProduction();
      return true;
    }
    if (upgradeId === 'helium-atom' && this.synthesizeElement('helium')) {
      upgrade.level += 1;
      this.updateProduction();
      return true;
    }
    if (upgradeId === 'carbon-atom' && this.synthesizeElement('carbon')) {
      upgrade.level += 1;
      this.updateProduction();
      return true;
    }
    if (upgradeId === 'nitrogen-atom' && this.synthesizeElement('nitrogen')) {
      upgrade.level += 1;
      this.updateProduction();
      return true;
    }
    if (upgradeId === 'oxygen-atom' && this.synthesizeElement('oxygen')) {
      upgrade.level += 1;
      this.updateProduction();
      return true;
    }
    if (upgradeId === 'iron-atom' && this.synthesizeElement('iron')) {
      upgrade.level += 1;
      this.updateProduction();
      return true;
    }

    // Fusion reactor costs energy
    if (upgradeId === 'fusion-reactor') {
      this.resources.energy -= cost;
      upgrade.level += 1;
      this.updateProduction();
      return true;
    }

    // Default: costs quarks
    this.resources.quarks -= cost;
    upgrade.level += 1;
    this.updateProduction();
    return true;
  }

  /**
   * Manual click to generate quarks
   */
  clickQuark(): number {
    const now = Date.now();
    
    // Update combo counter
    if (now - this.clickCombo.lastClickTime < this.COMBO_WINDOW) {
      this.clickCombo.count += 1;
    } else {
      // Reset combo if too much time passed
      this.clickCombo.count = 1;
      this.clickCombo.isActive = false;
    }
    
    this.clickCombo.lastClickTime = now;
    
    // Activate combo if threshold reached
    if (this.clickCombo.count >= this.COMBO_THRESHOLD && !this.clickCombo.isActive) {
      this.clickCombo.isActive = true;
      this.clickCombo.expiresAt = now + this.COMBO_DURATION;
    }
    
    // Check if combo expired
    if (this.clickCombo.isActive && now > this.clickCombo.expiresAt) {
      this.clickCombo.isActive = false;
      this.clickCombo.count = 0;
    }
    
    const clickPower = this.getClickPower();
    this.resources.quarks += clickPower;
    this.stats.totalQuarksCollected += clickPower;
    this.stats.totalClicks += 1;
    
    return clickPower;
  }

  /**
   * Calculate click power (base + bonuses)
   */
  getClickPower(): number {
    let power = 1; // Base click power
    
    // Permanent upgrade: Enhanced Click Power
    const clickPowerLevel = this.permanentUpgrades.get('click-power')?.level || 0;
    if (clickPowerLevel > 0) {
      power *= 1 + clickPowerLevel; // +100% per level
    }
    
    // Proton boost
    const protonBoost = 1 + (this.resources.protons * this.PROTON_BOOST_MULTIPLIER);
    power *= protonBoost;
    
    // Combo multiplier
    if (this.clickCombo.isActive) {
      power *= this.COMBO_MULTIPLIER;
    }

    return Math.floor(power);
  }
  
  /**
   * Get combo status for UI display
   */
  getComboStatus(): { count: number; isActive: boolean; progress: number } {
    const progress = Math.min(this.clickCombo.count / this.COMBO_THRESHOLD, 1);
    return {
      count: this.clickCombo.count,
      isActive: this.clickCombo.isActive,
      progress: progress,
    };
  }

  /**
   * Determine current game stage based on progress
   */
  updateGameStage(): GameStage {
    // Stage progression based on heaviest element
    if (this.resources.iron >= 1) {
      this.currentStage = 'supernova';
    } else if (this.resources.carbon >= 1) {
      this.currentStage = 'fusion';
    } else if (this.resources.hydrogen >= 1) {
      this.currentStage = 'atomic';
    } else {
      this.currentStage = 'subatomic';
    }
    
    return this.currentStage;
  }

  /**
   * Check if universe end should be triggered
   */
  checkUniverseEnd(): boolean {
    return this.resources.iron >= this.UNIVERSE_END_THRESHOLD_LEVEL && !this.universeEnd.isTriggered;
  }

  /**
   * Calculate neutron star matter reward based on progress
   */
  calculateNeutronStarReward(): number {
    // Base reward on total elements created
    const totalElements = 
      this.resources.hydrogen +
      this.resources.helium * 2 +
      this.resources.carbon * 5 +
      this.resources.nitrogen * 7 +
      this.resources.oxygen * 10 +
      this.resources.iron * 50;

    // Formula: sqrt(total / 10)
    return Math.floor(Math.sqrt(totalElements / 10));
  }

  /**
   * Trigger the universe end sequence
   */
  triggerUniverseEnd(): void {
    if (!this.universeEnd.isTriggered) {
      this.universeEnd.isTriggered = true;
      this.universeEnd.startTime = Date.now();
      this.universeEnd.phase = 'collapse';
    }
  }

  /**
   * Update universe end animation phase
   */
  updateUniverseEnd(): void {
    if (!this.universeEnd.isTriggered) return;

    const elapsed = Date.now() - this.universeEnd.startTime;

    // Phase timing (in milliseconds)
    if (elapsed < 3000) {
      this.universeEnd.phase = 'collapse'; // 0-3s: Collapse animation
    } else if (elapsed < 5000) {
      this.universeEnd.phase = 'singularity'; // 3-5s: Black hole singularity
    } else if (elapsed < 7000) {
      this.universeEnd.phase = 'rebirth'; // 5-7s: Show observer message
    } else {
      this.universeEnd.phase = 'complete'; // 7s+: Ready for reset
    }
  }

  /**
   * Reset universe (prestige/rebirth)
   */
  resetUniverse(): void {
    // Calculate and award neutron star matter
    const reward = this.calculateNeutronStarReward();
    const neutronStarKept = this.resources.neutronStarMatter + reward;

    // Increment cycle counter
    this.stats.universeCycles += 1;

    // Keep some stats
    const cyclesKept = this.stats.universeCycles;
    const startTimeKept = this.stats.gameStartTime;

    // Calculate starting quarks from permanent upgrade
    const startingQuarksLevel = this.permanentUpgrades.get('starting-quarks')?.level || 0;
    const startingQuarks = startingQuarksLevel * 100;

    // Reset resources (but keep neutron star matter)
    this.resources = {
      quarks: startingQuarks,
      protons: 0,
      neutrons: 0,
      energy: 0,
      neutronStarMatter: neutronStarKept,
      hydrogen: 0,
      helium: 0,
      carbon: 0,
      nitrogen: 0,
      oxygen: 0,
      iron: 0,
    };

    // Reset upgrades
    this.upgrades.forEach(upgrade => {
      upgrade.level = 0;
      upgrade.unlocked = upgrade.id === 'quantum-fluctuation' || upgrade.id === 'strong-interaction';
    });

    // Reset production
    this.production = {
      quarksPerSecond: 0,
      protonsPerSecond: 0,
      neutronsPerSecond: 0,
      energyPerSecond: 0,
    };

    // Reset stats (but keep cycles)
    this.stats = {
      totalQuarksCollected: 0,
      totalClicks: 0,
      totalProtonsSynthesized: 0,
      gameStartTime: startTimeKept,
      lastUpdateTime: Date.now(),
      universeCycles: cyclesKept,
    };

    // Reset stage
    this.currentStage = 'subatomic';

    // Reset universe end state
    this.universeEnd = {
      isTriggered: false,
      startTime: 0,
      phase: 'collapse',
    };

    // Reset combo
    this.clickCombo = {
      count: 0,
      lastClickTime: 0,
      isActive: false,
      expiresAt: 0,
    };

    // Reset energy boost
    this.energyBoost = {
      level: 0,
      isActive: false,
      totalEnergySpent: 0,
    };
  }

  /**
   * Calculate global multipliers from elements
   */
  private getGlobalMultipliers(): { quark: number; energy: number } {
    let quarkMult = 1;
    let energyMult = 1;

    // Helium: Doubles hydrogen production (affects energy)
    if (this.resources.helium > 0) {
      energyMult *= Math.pow(2, this.resources.helium);
    }

    // Carbon: Reduces upgrade costs (implemented in getUpgradeCost)
    // Nitrogen: Increases quark production
    if (this.resources.nitrogen > 0) {
      quarkMult *= 1 + (this.resources.nitrogen * 0.5); // +50% per nitrogen
    }

    // Oxygen: Boosts energy production
    if (this.resources.oxygen > 0) {
      energyMult *= 1 + (this.resources.oxygen * 0.75); // +75% per oxygen
    }

    // Iron: Massive boost but signals end game
    if (this.resources.iron > 0) {
      quarkMult *= 1 + (this.resources.iron * 2); // +200% per iron
      energyMult *= 1 + (this.resources.iron * 2);
    }

    return { quark: quarkMult, energy: energyMult };
  }

  /**
   * Update production rates based on current upgrades
   */
  updateProduction(): void {
    const multipliers = this.getGlobalMultipliers();

    // Permanent upgrade: Production Amplifier
    const productionBoostLevel = this.permanentUpgrades.get('production-boost')?.level || 0;
    let productionBoost = 1 + (productionBoostLevel * 0.5); // +50% per level

    // Development mode: boost production
    if (this.DEV_MODE) {
      productionBoost *= this.DEV_SPEED_MULTIPLIER;
    }

    // Energy boost multiplier - THIS IS THE KEY NEW FEATURE
    const energyBoostMultiplier = this.getEnergyBoostMultiplier();

    // Quantum Fluctuation production
    const quantumLevel = this.upgrades.get('quantum-fluctuation')?.level || 0;
    let quarksPerSec = quantumLevel * 1;
    quarksPerSec *= multipliers.quark;
    quarksPerSec *= productionBoost;
    quarksPerSec *= energyBoostMultiplier; // Apply energy boost
    
    // Auto-clicker
    const autoClickerLevel = this.permanentUpgrades.get('auto-clicker')?.level || 0;
    if (autoClickerLevel > 0) {
      quarksPerSec += autoClickerLevel * this.getClickPower() * energyBoostMultiplier;
    }
    
    this.production.quarksPerSecond = quarksPerSec;

    // Strong Interaction - auto-synthesize protons (also boosted by energy)
    const strongLevel = this.upgrades.get('strong-interaction')?.level || 0;
    this.production.protonsPerSecond = strongLevel * 0.1 * (this.DEV_MODE ? this.DEV_SPEED_MULTIPLIER : 1) * energyBoostMultiplier;

    // Neutron Generator (also boosted by energy)
    const neutronLevel = this.upgrades.get('neutron-generator')?.level || 0;
    this.production.neutronsPerSecond = neutronLevel * 0.1 * (this.DEV_MODE ? this.DEV_SPEED_MULTIPLIER : 1) * energyBoostMultiplier;

    // Energy production from elements
    let energyPerSec = 0;
    energyPerSec += this.resources.hydrogen * this.ELEMENTS.hydrogen.energy;
    energyPerSec += this.resources.helium * this.ELEMENTS.helium.energy;
    energyPerSec += this.resources.carbon * this.ELEMENTS.carbon.energy;
    energyPerSec += this.resources.nitrogen * this.ELEMENTS.nitrogen.energy;
    energyPerSec += this.resources.oxygen * this.ELEMENTS.oxygen.energy;
    energyPerSec += this.resources.iron * this.ELEMENTS.iron.energy;

    // Apply fusion reactor bonus
    const fusionLevel = this.upgrades.get('fusion-reactor')?.level || 0;
    if (fusionLevel > 0) {
      energyPerSec *= 1 + (fusionLevel * 0.5); // +50% per level
    }

    energyPerSec *= multipliers.energy;
    energyPerSec *= productionBoost;
    this.production.energyPerSecond = energyPerSec;
  }

  /**
   * Update game state (called every frame)
   */
  update(deltaTime: number): void {
    const dt = deltaTime / 1000; // Convert to seconds
    const now = Date.now();

    // Generate quarks from production
    const quarksGenerated = this.production.quarksPerSecond * dt;
    this.resources.quarks += quarksGenerated;
    this.stats.totalQuarksCollected += quarksGenerated;

    // Auto-synthesize protons if Strong Interaction is active
    const protonsGenerated = this.production.protonsPerSecond * dt;
    if (protonsGenerated > 0) {
      const quarksNeeded = protonsGenerated * this.QUARKS_PER_PROTON;
      if (this.resources.quarks >= quarksNeeded) {
        this.resources.quarks -= quarksNeeded;
        this.resources.protons += protonsGenerated;
        this.stats.totalProtonsSynthesized += protonsGenerated;
      }
    }

    // Auto-generate neutrons
    const neutronsGenerated = this.production.neutronsPerSecond * dt;
    if (neutronsGenerated > 0) {
      const quarksNeeded = neutronsGenerated * this.QUARKS_PER_NEUTRON;
      if (this.resources.quarks >= quarksNeeded) {
        this.resources.quarks -= quarksNeeded;
        this.resources.neutrons += neutronsGenerated;
      }
    }

    // Generate energy from elements
    const energyGenerated = this.production.energyPerSecond * dt;
    this.resources.energy += energyGenerated;

    // Consume energy for boost
    if (this.energyBoost.isActive && this.energyBoost.level > 0) {
      const energyToConsume = this.getEnergyBoostConsumption() * dt;
      if (this.resources.energy >= energyToConsume) {
        this.resources.energy -= energyToConsume;
        this.energyBoost.totalEnergySpent += energyToConsume;
      } else {
        // Not enough energy, disable boost
        this.energyBoost.level = 0;
        this.energyBoost.isActive = false;
      }
    }

    // Update production rates (in case proton count changed)
    this.updateProduction();

    // Check if combo expired
    if (this.clickCombo.isActive && now > this.clickCombo.expiresAt) {
      this.clickCombo.isActive = false;
      this.clickCombo.count = 0;
    }

    // Progressive unlock system
    if (this.upgrades.get('quantum-fluctuation')?.level! > 0) {
      this.upgrades.get('proton-synthesizer')!.unlocked = true;
    }

    if (this.resources.protons >= 1) {
      this.upgrades.get('hydrogen-atom')!.unlocked = true;
    }

    if (this.resources.hydrogen >= 1) {
      this.upgrades.get('neutron-generator')!.unlocked = true;
    }

    if (this.resources.neutrons >= 2 && this.resources.protons >= 2) {
      this.upgrades.get('helium-atom')!.unlocked = true;
    }

    if (this.resources.helium >= 1) {
      this.upgrades.get('carbon-atom')!.unlocked = true;
    }

    if (this.resources.carbon >= 1) {
      this.upgrades.get('nitrogen-atom')!.unlocked = true;
      this.upgrades.get('fusion-reactor')!.unlocked = true;
    }

    if (this.resources.nitrogen >= 1) {
      this.upgrades.get('oxygen-atom')!.unlocked = true;
    }

    if (this.resources.oxygen >= 1) {
      this.upgrades.get('iron-atom')!.unlocked = true;
    }

    // Update game stage
    this.updateGameStage();

    // Update universe end sequence if triggered
    this.updateUniverseEnd();

    // Check if universe end should be triggered
    if (this.checkUniverseEnd()) {
      this.triggerUniverseEnd();
    }

    this.stats.lastUpdateTime = Date.now();
  }

  /**
   * Save game state to localStorage
   */
  save(): void {
    const saveData = {
      resources: this.resources,
      upgrades: Array.from(this.upgrades.entries()).map(([id, upgrade]) => ({
        id,
        level: upgrade.level,
        unlocked: upgrade.unlocked,
      })),
      permanentUpgrades: Array.from(this.permanentUpgrades.entries()).map(([id, upgrade]) => ({
        id,
        level: upgrade.level,
      })),
      stats: this.stats,
      clickCombo: this.clickCombo,
      universeEnd: this.universeEnd,
      energyBoost: this.energyBoost,
    };

    localStorage.setItem('elementZeroSave', JSON.stringify(saveData));
  }

  /**
   * Load game state from localStorage
   */
  load(): boolean {
    const saveData = localStorage.getItem('elementZeroSave');
    if (!saveData) return false;

    try {
      const data = JSON.parse(saveData);

      // Restore resources
      this.resources = data.resources;

      // Restore upgrades
      data.upgrades.forEach((savedUpgrade: any) => {
        const upgrade = this.upgrades.get(savedUpgrade.id);
        if (upgrade) {
          upgrade.level = savedUpgrade.level;
          upgrade.unlocked = savedUpgrade.unlocked;
        }
      });

      // Restore stats
      this.stats = data.stats;

      // Restore permanent upgrades (if exists)
      if (data.permanentUpgrades) {
        data.permanentUpgrades.forEach((savedUpgrade: any) => {
          const upgrade = this.permanentUpgrades.get(savedUpgrade.id);
          if (upgrade) {
            upgrade.level = savedUpgrade.level;
          }
        });
      }

      // Restore click combo (if exists)
      if (data.clickCombo) {
        this.clickCombo = data.clickCombo;
        // Reset active status on load (combo expired during offline)
        this.clickCombo.isActive = false;
      }

      // Restore universe end state (if exists)
      if (data.universeEnd) {
        this.universeEnd = data.universeEnd;
        // Don't resume animation on load
        this.universeEnd.isTriggered = false;
      }

      // Restore energy boost state (if exists)
      if (data.energyBoost) {
        this.energyBoost = data.energyBoost;
      }

      // Recalculate production
      this.updateProduction();

      return true;
    } catch (error) {
      console.error('Failed to load save data:', error);
      return false;
    }
  }

  /**
   * Reset game state (for prestige/supernova)
   */
  reset(): void {
    this.resources = {
      quarks: 0,
      protons: 0,
      neutrons: 0,
      energy: 0,
      neutronStarMatter: 0,
      hydrogen: 0,
      helium: 0,
      carbon: 0,
      nitrogen: 0,
      oxygen: 0,
      iron: 0,
    };

    this.upgrades.forEach(upgrade => {
      upgrade.level = 0;
      upgrade.unlocked = upgrade.id === 'quantum-fluctuation' || upgrade.id === 'strong-interaction';
    });

    this.production = {
      quarksPerSecond: 0,
      protonsPerSecond: 0,
      neutronsPerSecond: 0,
      energyPerSecond: 0,
    };

    this.stats = {
      totalQuarksCollected: 0,
      totalClicks: 0,
      totalProtonsSynthesized: 0,
      gameStartTime: Date.now(),
      lastUpdateTime: Date.now(),
      universeCycles: 0,
    };
  }

  /**
   * Hard reset - clear everything including permanent upgrades
   */
  hardReset(): void {
    // Reset all permanent upgrades
    this.permanentUpgrades.forEach(upgrade => {
      upgrade.level = 0;
    });

    // Reset everything else
    this.resources = {
      quarks: 0,
      protons: 0,
      neutrons: 0,
      energy: 0,
      neutronStarMatter: 0,
      hydrogen: 0,
      helium: 0,
      carbon: 0,
      nitrogen: 0,
      oxygen: 0,
      iron: 0,
    };

    this.upgrades.forEach(upgrade => {
      upgrade.level = 0;
      upgrade.unlocked = upgrade.id === 'quantum-fluctuation' || upgrade.id === 'strong-interaction';
    });

    this.production = {
      quarksPerSecond: 0,
      protonsPerSecond: 0,
      neutronsPerSecond: 0,
      energyPerSecond: 0,
    };

    this.stats = {
      totalQuarksCollected: 0,
      totalClicks: 0,
      totalProtonsSynthesized: 0,
      gameStartTime: Date.now(),
      lastUpdateTime: Date.now(),
      universeCycles: 0,
    };

    this.clickCombo = {
      count: 0,
      lastClickTime: 0,
      isActive: false,
      expiresAt: 0,
    };

    this.currentStage = 'subatomic';

    this.universeEnd = {
      isTriggered: false,
      startTime: 0,
      phase: 'collapse',
    };

    // Reset energy boost
    this.energyBoost = {
      level: 0,
      isActive: false,
      totalEnergySpent: 0,
    };

    // Clear save data
    localStorage.removeItem('elementZeroSave');
  }
}

